/**
 * Attach Bearer (signed-in) or x-api-key (anonymous public routes) to axios calls
 * targeting the Express API. Fixes 403 on private routes after apiAccessGate hardening.
 */
import axios from "axios";

import { backendRequestHeaders } from "./backendApiHeaders";
import { getApiBaseUrl } from "./apiUrl";

const INSTALLED = Symbol("afomaBackendAxiosAuthInstalled");

function resolveBackendOrigin() {
  const base = getApiBaseUrl() || process.env.NEXT_PUBLIC_BASE_URL || "";
  if (!base) {
    return "";
  }
  try {
    return new URL(base).origin;
  } catch {
    return "";
  }
}

function requestTargetsBackend(config, backendOrigin) {
  if (!backendOrigin) {
    return false;
  }

  const rawUrl = config.url ?? "";
  if (typeof rawUrl === "string" && rawUrl.startsWith("http")) {
    try {
      return new URL(rawUrl).origin === backendOrigin;
    } catch {
      return false;
    }
  }

  const baseURL = config.baseURL ?? axios.defaults.baseURL ?? "";
  if (typeof baseURL === "string" && baseURL.startsWith("http")) {
    try {
      if (new URL(baseURL).origin === backendOrigin) {
        return true;
      }
    } catch {
      return false;
    }
  }

  if (typeof rawUrl === "string" && rawUrl.startsWith("/")) {
    return true;
  }

  return false;
}

function mergeAuthHeaders(config) {
  const existing =
    config.headers && typeof config.headers.toJSON === "function"
      ? config.headers.toJSON()
      : { ...(config.headers || {}) };

  const authHeaders = backendRequestHeaders(existing);

  if (typeof config.headers?.set === "function") {
    Object.entries(authHeaders).forEach(([key, value]) => {
      if (value != null && value !== "") {
        config.headers.set(key, value);
      }
    });
    if (authHeaders.Authorization) {
      config.headers.delete("x-api-key");
    }
    return config;
  }

  config.headers = { ...existing, ...authHeaders };
  if (authHeaders.Authorization) {
    delete config.headers["x-api-key"];
  }
  return config;
}

export function attachBackendAxiosAuth(instance) {
  if (!instance || instance[INSTALLED]) {
    return;
  }
  instance[INSTALLED] = true;

  const backendOrigin = resolveBackendOrigin();

  instance.interceptors.request.use((config) => {
    if (!requestTargetsBackend(config, backendOrigin)) {
      return config;
    }
    return mergeAuthHeaders(config);
  });
}

let defaultAxiosPatched = false;

export function installBackendAxiosAuth() {
  if (typeof window === "undefined") {
    return;
  }

  attachBackendAxiosAuth(axios);

  if (!defaultAxiosPatched) {
    defaultAxiosPatched = true;
    const originalCreate = axios.create.bind(axios);
    axios.create = function createWithBackendAuth(config) {
      const instance = originalCreate(config);
      attachBackendAxiosAuth(instance);
      return instance;
    };
  }
}
